/**
 * Validacoes de telefone usadas em formularios.
 *
 * Regras (padrao Brasil pos-2014):
 *   FIXO    -> DDD (2) + 8 digitos = 10 digitos. Primeiro digito apos o DDD
 *              NAO pode ser 9 (do contrario eh celular truncado).
 *   CELULAR -> DDD (2) + 9 digitos = 11 digitos. Primeiro digito apos o DDD
 *              DEVE ser 9.
 *
 * Strings vazias ou nulas passam (campo nao preenchido — uso opcional).
 * Strings com `_` (placeholder da mascara) sao consideradas incompletas.
 */

export const normalizePhone = (value) =>
  value ? String(value).replace(/\D/g, '') : '';

export const isMaskIncomplete = (value) =>
  typeof value === 'string' && value.includes('_');

export const isEmptyPhone = (value) =>
  !value || normalizePhone(value).length === 0;

export const isValidFixedPhone = (value) => {
  const digits = normalizePhone(value);
  if (digits.length !== 10) return false;
  if (digits[2] === '9') return false; // celular truncado no campo de fixo
  return true;
};

export const isValidMobilePhone = (value) => {
  const digits = normalizePhone(value);
  if (digits.length !== 11) return false;
  if (digits[2] !== '9') return false;
  return true;
};

export const FIXED_PHONE_MESSAGE =
  'Telefone fixo: 8 dígitos após o DDD e não pode começar com 9.';

export const MOBILE_PHONE_MESSAGE =
  'Celular: 9 dígitos após o DDD e deve começar com 9.';

export const INCOMPLETE_PHONE_MESSAGE = 'Número incompleto.';

export const DUPLICATE_PHONE_MESSAGE =
  'Esse número já foi informado em outro campo de telefone.';

/**
 * Test do Yup para telefone fixo (campo opcional).
 * Use como: .test('phone-fixed', null, fixedPhoneYupTest)
 */
export const fixedPhoneYupTest = function fixedPhoneYupTest(value) {
  if (isEmptyPhone(value)) return true;
  if (isMaskIncomplete(value)) {
    return this.createError({ message: INCOMPLETE_PHONE_MESSAGE });
  }
  if (!isValidFixedPhone(value)) {
    return this.createError({ message: FIXED_PHONE_MESSAGE });
  }
  return true;
};

/**
 * Test do Yup para celular (campo opcional).
 * Use como: .test('phone-mobile', null, mobilePhoneYupTest)
 */
export const mobilePhoneYupTest = function mobilePhoneYupTest(value) {
  if (isEmptyPhone(value)) return true;
  if (isMaskIncomplete(value)) {
    return this.createError({ message: INCOMPLETE_PHONE_MESSAGE });
  }
  if (!isValidMobilePhone(value)) {
    return this.createError({ message: MOBILE_PHONE_MESSAGE });
  }
  return true;
};

/**
 * Test do Yup para entradas indexadas em phones[]:
 * - phones[0] eh telefone fixo
 * - phones[1..N] sao celulares
 * Le o indice a partir do `this.path` do Yup.
 */
export const phoneByIndexYupTest = function phoneByIndexYupTest(value) {
  const match = this.path.match(/\[(\d+)\]/);
  const idx = match ? parseInt(match[1], 10) : -1;
  return idx === 0
    ? fixedPhoneYupTest.call(this, value)
    : mobilePhoneYupTest.call(this, value);
};

/**
 * Test cross-field do Yup: garante que nenhum numero de telefone aparece
 * em mais de um campo do mesmo formulario.
 *
 * Recebe um array de { path, value } com todos os campos de telefone do
 * formulario. Retorna erro apontando o segundo campo que duplicou (deixa
 * o primeiro como "fonte de verdade", o segundo como "duplicado").
 */
export const noDuplicatePhonesYupTest = function noDuplicatePhonesYupTest(
  fields
) {
  const normalized = fields.map((f) => ({
    path: f.path,
    digits: normalizePhone(f.value),
  }));
  const filled = normalized.filter((f) => f.digits.length > 0);
  if (filled.length < 2) return true;

  const seen = new Map();
  for (let i = 0; i < filled.length; i += 1) {
    if (seen.has(filled[i].digits)) {
      return this.createError({
        path: filled[i].path,
        message: DUPLICATE_PHONE_MESSAGE,
      });
    }
    seen.set(filled[i].digits, true);
  }
  return true;
};
